'use strict';
import { cn_fence_opening_type } from '..';
import { cn_opening } from '../model/cn_opening';
import { cn_scene } from '../model/cn_scene';
import { cn_edition_handler } from './cn_edition_handler';
import { cn_svg_map } from './cn_svg_map';
import { cn_mouse_event } from './cn_mouse_event';
import { cn_add, cn_box, cn_dot, cn_middle, cn_mul, cn_normalize, cn_size, cn_sub } from '../utils/cn_utilities';
import { cn_pastille } from './cn_pastille';
import { cn_type_pastille } from './cn_type_pastille';
import { cn_edit_box } from './cn_edit_box';
import { cn_number_input, cn_number_list_input } from './cn_inputs';

export class cn_opening_handler extends cn_edition_handler {
    //***********************************************************************************
    /**
     * Constructor
     * @param {Array<cn_opening>} openings
     * @param {cn_svg_map} map
     * @param {boolean} creation
     */
    constructor(openings, map, creation = false) {
        super(openings, map);
        const obj = this;

        this._map = map;
        this._scene = this._map._scene;
        this._transaction_manager = this._map._building.transaction_manager;

        this._openings = openings;
        this._opening = (openings.length == 1) ? openings[0] : null;

        //** mouse flags */
        this._mouseover = -1;
        this._mouseover_opening = false;

        this._center = [0, 0];
        this._dx = [0, 0];
        this._dy = [0, 0];

        this._radius = 0;
        this._draw_disk = true;

        this._start = [true, false, false, true];
        this._inside = [false, true, false, true];

        this._measures = [];

        this._grabbed = false;

        this._draw_measures = true;

        this._opening_measures = [
            { selected: false, position: [0, 0, 0], reverse: false },
            { selected: false, position: [0, 0, 0], reverse: false },
            { selected: false, position: [0, 0, 0], reverse: true },
            { selected: false, position: [0, 0, 0], reverse: true }];
        this._opening_dimensions = { selected: false, position: [0, 0, 0] };

        // @type{selected: boolean, reverse: boolean, position; number[]}
        this._mouseover_measure = null;

        this._edit_box = null;
        this._build_edit_box();

        this.transaction_increment = 0;

        this._need_find_ambiguous_openings = true;
        this._ambiguous_openings = [];
        this._ambiguous_boxes = [];
        this._find_ambiguous_openings();
    }

    _find_ambiguous_openings() {
        if (!this._need_find_ambiguous_openings) return;
        this._ambiguous_openings = [];
        this._ambiguous_box_mouseover = -1;
        if (this._opening) {
            let x0 = this._opening.position;
            let x1 = x0 + this._opening.get_width();
            if (this._opening.wall) {
                this._ambiguous_openings = this._opening.wall.openings.filter(op => op.valid && op != this._opening && op.position < x1 && op.position + op.get_width() > x0);
                if (this._ambiguous_openings.length)
                    this._ambiguous_openings.push(this._opening);
            }
        }
        this._need_find_ambiguous_openings = false;
    }

    _build_edit_box() {

        //*** Edit box for mass selection */
        const edit_box = new cn_edit_box(this, this._openings, this._readOnly);
        this._edit_box = edit_box;
        this._handlers = [edit_box];

        //*** axis pastille */
        if (!this._readOnly) {
            //*** Dimensions pastille */
            const dimensions_pastille = new cn_pastille([0, 0], 'tape_measure.svg');
            edit_box.add_pastille(dimensions_pastille);
            dimensions_pastille.svg_class = 'pastille_background white';
            dimensions_pastille.title = 'Dimensions';
            dimensions_pastille.clicked = () => {
                this._select_opening_dimensions();
            }

            //*** Type pastille */
            const is_fence = this._openings[0].opening_type.constructor === cn_fence_opening_type;
            const type_pastille = new cn_type_pastille(this._openings, 'opening_type', () => {
                return !is_fence ?
                    this._map._building.get_opening_types() : this._map._building.get_fence_opening_types();
            }, !is_fence ? 'Type de baie' : 'Type de clôture', this._map);
            type_pastille.title = 'Modifier le modèle de l\'ouvrant';
            type_pastille.checkbox_label = "Conserver les dimensions";
            edit_box.add_pastille(type_pastille);
            type_pastille.on("clicked", () => {
                type_pastille.checkbox_status = false;
                this._openings.forEach(opening => opening["old_opening_type"] = opening.opening_type);
            });
            type_pastille.on("callback", () => {
                this._openings.forEach(opening => {
                    this._transaction_manager.push_item_set(opening, ["width", "height", "z"]);
                    if (type_pastille.checkbox_status) {
                        if (opening.width < 0) opening.width = opening["old_opening_type"].width;
                        if (opening.height < 0) opening.height = opening["old_opening_type"].height;
                        if (opening.z < 0) opening.z = opening["old_opening_type"].z;
                    }
                    else {
                        opening.width = -1;
                        opening.height = -1;
                        opening.z = -1;
                    }
                });
            });

            //**** Axis pastille */
            const axis_pastille = new cn_pastille([0, 0], 'switch_opening_positions.png');
            edit_box.add_pastille(axis_pastille);
            axis_pastille.svg_class = 'pastille_background white';
            axis_pastille.title = 'Modifier la position de l\'ouvrant';
            axis_pastille.clicked = () => {

                this._transaction_manager.push_transaction('Positionnement d\'ouvrant');
                const new_position = (this._openings[0].opening_position + 1) % 3;
                this._openings.forEach(opening => {
                    this._transaction_manager.push_item_set(opening, ['opening_position']);
                    opening.opening_position = new_position;
                    opening.wall.update_openings();
                });
                this._scene.update_space_numerotation();
                this.call('change');
            }

            const info_pastille = new cn_pastille([0, 0], 'information.svg');
            edit_box.add_pastille(info_pastille);
            info_pastille.svg_class = 'pastille_background white';
            info_pastille.title = 'Détails du type';
            info_pastille.clicked = () => {
                this._parent.call('show_type_information', this._openings[0].get_opening_type());
            };
            info_pastille.visible = !this._openings.some(op => op.get_opening_type() != this._openings[0].get_opening_type());

            edit_box.add_lock_pastille(this._transaction_manager);
            edit_box.add_duplicator_pastille();
        }

        edit_box.add_select_siblings_pastille('opening_type');

    }

    _select_opening_dimensions() {

        const input_list = [];

        const input_width = new cn_number_input('Largeur', this._openings[0].get_width(), 'm', 2, 0.1, 10);
        const variable_width = this._openings.some(s => s.width != this._openings[0].width);
        input_width.checkbox_label = (variable_width) ? "Variable" : "Automatique";
        input_width.checkbox_status = (variable_width || (this._openings[0].width < 0));
        input_list.push(input_width);

        const input_height = new cn_number_input('Hauteur', this._openings[0].get_height(), 'm', 2, 0.1, 10);
        const variable_height = this._openings.some(s => s.height != this._openings[0].height);
        input_height.checkbox_label = (variable_height) ? "Variable" : "Automatique";
        input_height.checkbox_status = (variable_height || (this._openings[0].height < 0));
        input_list.push(input_height);

        let input_z = null;
        let variable_z = false;
        if (this._openings.some(o => o.opening_type.category == "window")) {
            input_z = new cn_number_input('Décalage', this._openings[0].get_z(), 'm', 2, 0, 10);
            variable_z = this._openings.some(s => s.z != this._openings[0].z);
            input_z.checkbox_label = (variable_z) ? "Variable" : "Automatique";
            input_z.checkbox_status = (variable_z || (this._openings[0].z < 0));
            input_list.push(input_z);
        }

        const input = new cn_number_list_input('Dimensions', input_list);
        input.callback = () => {
            this._transaction_manager.push_transaction('Dimensions de baies');
            this._openings.forEach(opening => {
                this._transaction_manager.push_item_set(opening, ['width', 'height', 'z']);
                if (!variable_width)
                    opening.width = (input_width.checkbox_status) ? -1 : input_width.value;
                else if (!input_width.checkbox_status)
                    opening.width = input_width.value;

                if (!variable_height)
                    opening.height = (input_height.checkbox_status) ? -1 : input_height.value;
                else if (!input_height.checkbox_status)
                    opening.height = input_height.value;

                if (input_z) {
                    if (!variable_z)
                        opening.z = (input_z.checkbox_status) ? -1 : input_z.value;
                    else if (!input_z.checkbox_status)
                        opening.z = input_z.value;
                }

            });
            this._scene.update_space_numerotation();
            this._map.refresh();
        };
        this._map.call('number_list_input', input);
    }

    //***********************************************************************************
    //**** Force one measure
    //***********************************************************************************
    set_current_measure(v) {
        if (!this._mouseover_measure) return false;

        var delta = v - this._mouseover_measure.position[2];
        if (this._mouseover_measure.reverse) delta = -delta;
        var prev_pos = this._opening.position;
        this._opening.position += delta;
        if (!this._opening.check_wall()) {
            this._opening.position = prev_pos;
            return false;
        }

        this._opening.position = prev_pos;
        this._transaction_manager.push_transaction('Positionnement d\'ouvrant', this._opening.ID);
        this._transaction_manager.push_item_set(this._opening, 'position', () => {
            this._opening.update_wall_position();
            this._opening.wall.update_openings();
        });
        this._opening.position += delta;
        this._opening.update_wall_position();
        this._opening.wall.update_openings();
        this._scene.update_space_numerotation();
        this._map.refresh();
        this.call('change');
        return true;
    }

    //***********************************************************************************
    //**** Draws
    //***********************************************************************************
    draw(camera, draw_measures = true) {

        if (this._opening == null) return super.draw(camera);

        if (this._edit_box.duplication_mode())
            return this._edit_box.draw(camera);

        var html = '';

        var measure_opening = this._opening;
        var wall = measure_opening.wall;
        this._draw_measures = draw_measures;

        let html_measures = measure_opening.draw_measures(camera, [], this._opening_dimensions, this._opening_measures);

        //*** draw oreitnation ad inside disk
        this._radius = this._opening.get_width() * camera.world_to_screen_scale;
        if (this._radius < camera.thumb_size) this._radius = camera.thumb_size;
        this._radius = 100;

        var c = cn_add(cn_middle(wall.bounds.pmin, wall.bounds.pmax), cn_mul(wall.bounds.direction, this._opening.position + 0.5 * this._opening.get_width()));
        this._center = camera.world_to_screen(c);
        this._dx = cn_sub(camera.world_to_screen(cn_add(c, wall.bounds.direction)), this._center);
        cn_normalize(this._dx);
        this._dx = cn_mul(this._dx, this._radius);
        var dy = cn_sub(camera.world_to_screen(cn_add(c, wall.bounds.normal)), this._center);
        cn_normalize(dy);
        this._dy = cn_mul(dy, this._radius);
        this._dx = cn_mul(this._dx, -1);

        const x_symetry = this._opening.opening_type.get_x_symetry();
        const y_symetry = this._opening.opening_type.get_y_symetry();
        if (this._opening.valid && (!x_symetry || !y_symetry)) {
            this.draw_disk = true;

            var points = [];
            points.push(cn_add(this._center, this._dx));
            points.push(cn_add(this._center, this._dy));
            points.push(cn_sub(this._center, this._dx));
            points.push(cn_sub(this._center, this._dy));

            this._opening_position = -1;
            if (this._opening.opening_start) this._opening_position++;
            if (this._opening.opening_inside) this._opening_position = 3 - this._opening_position;

            const was_opening_start = this._opening.opening_start;
            const was_opening_inside = this._opening.opening_inside;

            for (var n = 0; n < 4; n++) {
                const p0 = points[n];
                const p1 = points[(n + 1) % 4];

                var mouseover = false;
                var selected = false;

                if (n == this._mouseover) mouseover = true;
                ;
                if (was_opening_start == this._start[n] && was_opening_inside == this._inside[n]) {
                    this._opening_position = n;
                    selected = true;
                }

                var n1 = n;
                if (x_symetry || y_symetry) {
                    n1 = (x_symetry) ? (n < 2) ? 1 - n : 5 - n : 3 - n;
                    selected = selected || (was_opening_start == this._start[n1] && was_opening_inside == this._inside[n1]);
                    mouseover = mouseover || (n1 == this._mouseover);
                }

                var extra = '';
                if (mouseover) extra += ' mouseover';
                if (selected) extra += ' selected';
                html += `<path class="opening_disk${extra}" d="M ${this._center[0]} ${this._center[1]} L ${p1[0]} ${p1[1]} A ${this._radius} ${this._radius} 0 0 0 ${p0[0]} ${p0[1]} Z" />`;

                if (y_symetry && n1 < n) continue;
                if (x_symetry && n1 > n) continue;
                this._opening.opening_start = this._start[n];
                this._opening.opening_inside = this._inside[n];
                const nx = (x_symetry) ? 0 : (n == 0 || n == 3) ? 0.4 : -0.4;
                const ny = (x_symetry) ? (n < 2) ? 0.4 : -0.4 : (n < 2) ? 0.5 : -0.5;
                html += `<g opacity="0.5" transform="translate(${this._center[0] + this._dx[0] * nx + this._dy[0] * ny},${this._center[1] + this._dx[1] * nx + this._dy[1] * ny}) scale(0.3,0.3)  translate(${-this._center[0]},${-this._center[1]})">`;
                html += this._opening.draw(camera, []);
                html += `</g>`;
            }
            this._opening.opening_start = was_opening_start;
            this._opening.opening_inside = was_opening_inside;
        } else
            this._draw_disk = false;

        var world_radius = this._radius / camera.world_to_screen_scale;
        var dist = this._opening.wall.wall_type.thickness;
        if (this._draw_disk) dist += world_radius;

        //*** Draw wall locks */
        this._opening.get_candidate_locks().forEach((v, vindex) => {
            html += camera.draw_lock(v.position, v == this._opening.wall_lock_vertex, this._mouseover == 100 + vindex);
        });
        /*this._opening.wall.vertices.forEach((v, vindex) => {
            html += camera.draw_lock(v.position, vindex == this._opening.wall_lock, this._mouseover == 10 + vindex);
        });*/

        //*** draw opening */
        if (this._mouseover_opening)
            html += this._opening.draw(camera, ['mouseover']);
        else
            html += this._opening.draw(camera, ['selected']);

        //*** Draw move handles */
        if (this._opening.valid) {
            for (var nv = 0; nv < 2; nv++) {
                let x = this._opening.position;
                if (nv) x += this._opening.get_width();
                let p0 = this._opening.wall.compute_side_position_from_abcissa(x, 0);
                let p1 = this._opening.wall.compute_side_position_from_abcissa(x, 1);

                if (this._mouseover == nv + 20)
                    html += camera.draw_line_move_arrow(p0, p1);
            }
        }

        //*** draw ambiguities */
        this._find_ambiguous_openings();
        this._ambiguous_boxes = [];
        if (this._ambiguous_openings.length) {
            let box = new cn_box();
            this._ambiguous_openings.forEach(op => {
                let z0 = op.compute_offset() + op.get_z();
                box.enlarge_point([op.position, z0]);
                box.enlarge_point([op.position + op.get_width(), z0 + op.get_height()]);
                box.enlarge_distance(0.2);
            });
            let screen_size = 100;
            let scale = Math.max(box.size[0] / screen_size, box.size[1] / screen_size);
            let c0 = cn_sub([this._center[0] + this._radius, this._center[1] - this._radius], [0.2 * box.size[0] / scale, 0.2 * box.size[1] / scale]);
            html += `<rect x="${c0[0]}" y="${c0[1]}" width="${box.size[0] / scale}" height="${box.size[1] / scale}" fill="white" stroke="black" />`;
            console.log("Ambiguous");
            this._ambiguous_openings.forEach((op, idx) => {
                let z0 = op.compute_offset() + op.get_z();
                let p = cn_sub([op.position, z0], box.posmin);
                //html += `<rect x="${c0[0] + p[0] / scale}" y="${c0[1] + (box.size[1] - p[1] - op.get_height()) / scale} " width="${op.get_width() / scale} " height="${op.get_height() / scale} " fill="${op == this._opening ? "red" : "grey"} " stroke="black" />`;
                let rect = [c0[0] + p[0] / scale, c0[1] + (box.size[1] - p[1] - op.get_height()) / scale, op.get_width() / scale, op.get_height() / scale];
                html += `<g transform="translate(${rect[0]},${rect[1]})">`;
                html += op.get_opening_type().draw_svg_icon(rect[2], rect[3]);
                if (op == this._opening)
                    html += `<rect x="0" y="0" width="${rect[2]}" height="${rect[3]}" fill="none" stroke="red" stroke-width="2" />`;
                if (idx == this._ambiguous_box_mouseover)
                    html += `<rect x="0" y="0" width="${rect[2]}" height="${rect[3]}" fill="none" stroke="yellow" stroke-width="2" />`;
                html += `</g>`;
                let bx = new cn_box();
                bx.enlarge_point([rect[0], rect[1]]);
                bx.enlarge_point([rect[0] + rect[2], rect[1] + rect[3]]);
                this._ambiguous_boxes.push(bx);
            });
        }

        //*** rec */
        html += super.draw(camera);
        html += html_measures;

        //*** Draw opening measure */
        if (this._opening.valid) {
            let p0 = this._opening.wall.compute_side_position_from_abcissa(this._opening.position, 1);
            let p1 = this._opening.wall.compute_side_position_from_abcissa(this._opening.position + this._opening.get_width(), 1);
        }
        return html;
    }

    //***********************************************************************************
    //**** clear move effects
    //***********************************************************************************
    clear_move() {
        this._mouseover_opening = false;
        this._mouseover = -1;
        this._mouseover_measure = null;
        this._ambiguous_box_mouseover = -1;
        super.clear_move();
    }

    /**
     * Manage a click. Only after a grab that returned true, and no drag. To return 'true' if click had an effect.
     * @param {cn_mouse_event} mouse_event
     * @returns {boolean}
     */
    click(mouse_event) {

        if (this._edit_box.duplication_mode())
            return this._edit_box.click(mouse_event);

        //*** Click on edit box */
        if (super.click(mouse_event)) return true;

        if (this._map.get_readonly()) {
            return false;
        }

        //*** Click on ambiguous window */
        if (this._ambiguous_box_mouseover >= 0) {
            let opening = this._ambiguous_openings[this._ambiguous_box_mouseover];
            if (opening && opening != this._opening) {
                this._opening = opening;
                this._openings = [opening];
                this._need_find_ambiguous_openings = true;
                this._build_edit_box();
                return true;
            }
        }

        //*** click on measures */
        this.move(mouse_event);
        if (this._mouseover_measure) {
            if (this._mouseover_measure == this._opening_dimensions) {
                this._select_opening_dimensions();
                return true;
            }
            const input = new cn_number_input('Modifier la distance', this._mouseover_measure.position[2]);
            input.callback = () => {
                this.set_current_measure(input.value);
            };
            input.decimals = 3;
            input.unit = 'm';
            input.min_value = 0;
            this.call('number_input', input);
            this._need_find_ambiguous_openings = true;
            return true;
        }

        //*** click on disk */
        if (this._mouseover >= 0) {

            //*** Wall lock was clicked */
            if (this._mouseover >= 100) {
                this._transaction_manager.push_transaction('Lien d\'ouvrant');
                this._transaction_manager.push_item_set(this._opening, ['wall_lock_vertex', 'wall_position', 'position']);
                this._opening.wall_lock_vertex = this._opening.get_candidate_locks()[this._mouseover - 100];
                this._opening.update_wall_position();
            }

            if (this._mouseover == this._opening_position) return true;
            this._transaction_manager.push_transaction('Orientation d\'ouvrant');
            this._transaction_manager.push_item_set(this._opening, ['opening_start', 'opening_inside']);

            this._opening.opening_start = this._start[this._mouseover];
            this._opening.opening_inside = this._inside[this._mouseover];
            if (this._scene.constructor == cn_scene) this._scene.update_space_numerotation();
            this.call('change');
            this._need_find_ambiguous_openings = true;
            return true;
        }
        return false;
    }

    grab(mouse_event) {
        if (this._edit_box.duplication_mode())
            return this._edit_box.grab(mouse_event);

        this._grabbed = false;
        if (super.grab(mouse_event)) return true;

        //***Grab resize arrows ?  */
        if (this._mouseover == 20 || this._mouseover == 21) {
            this._grabbed = true;
            return true;
        }

        this._grabbed = (this._opening && this._opening.contains(mouse_event.mouse_world, mouse_event.camera.snap_world_distance));
        this.transaction_increment = (this.transaction_increment + 1) % 100;
        return this.move(mouse_event);
    }

    drag(ev) {
        if (this._edit_box.duplication_mode())
            return this._edit_box.drag(ev);

        if (!this._grabbed) {
            if (super.drag(ev)) return true;
            return false;
        }

        var opening = this._opening;
        if (opening.locked) return false;

        var old_position = opening.wall_position;

        /** Resize ?  */
        if (this._mouseover == 20 || this._mouseover == 21) {
            let a = opening.wall.compute_abcissa(ev.mouse_world);
            let z0 = opening.compute_offset() + opening.get_z();
            let z1 = z0 + opening.get_height();
            let wall_openings = opening.wall.openings.filter(op => {
                let zz0 = op.compute_offset() + op.get_z();
                let zz1 = zz0 + op.get_height();
                return !(z0 >= zz1 || zz0 >= z1);
            });

            let oi = wall_openings.indexOf(opening);
            let opening_prec = (oi > 0) ? wall_openings[oi - 1] : null;
            let amin = (opening_prec) ? opening_prec.position + opening_prec.get_width() : opening.wall.opening_area[0];
            if (a < amin) a = amin;
            let opening_next = (oi < wall_openings.length - 1) ? wall_openings[oi + 1] : null;
            let amax = (opening_next) ? opening_next.position : opening.wall.opening_area[1];
            if (a > amax) a = amax;

            let new_position = opening.position;
            let old_width = opening.get_width();
            let new_width = opening.get_width();
            if (this._mouseover == 20) {
                new_position = a;
                new_width = opening.position + old_width - a;
            }
            else {
                new_width = a - opening.position;
            }
            if (new_width > 0.1) {
                this._transaction_manager.push_transaction('Déplacement d\'ouvrant', opening.ID + this.transaction_increment, () => {
                    opening.wall.update_openings();
                });
                this._transaction_manager.push_item_set(opening, ['position', 'width']);
                opening.position = new_position;
                opening.width = new_width;
                opening.update_wall_position();
                opening.wall.update_openings();
                this._scene.update_space_numerotation();
            }
            this._need_find_ambiguous_openings = true;
            return true;
        }

        var old_wall = opening.wall;
        var new_wall = this._scene.find_wall(ev.mouse_world);
        if (new_wall && new_wall != old_wall)
            opening.wall = new_wall;
        else
            new_wall = old_wall;
        if (!opening.optimize_placement(ev.mouse_world, ev.camera.snap_world_distance)) {
            opening.wall_position = old_position;
            opening.wall = old_wall;
        } else {
            var new_position = opening.wall_position;
            opening.wall_position = old_position;
            opening.wall = old_wall;
            this._transaction_manager.push_transaction('Déplacement d\'ouvrant', opening.ID + this.transaction_increment, () => {
                opening.wall.update_openings();
            });
            this._transaction_manager.push_item_set(opening, 'wall_position');
            opening.wall_position = new_position;
            opening.set_wall(new_wall, this._transaction_manager);
            opening.wall.update_openings();
            this._scene.update_space_numerotation();
            this.call('change');
            this._need_find_ambiguous_openings = true;
        }
        return true;
    }

    /**
     * Manage a passive move. To return 'true' if something of interest under the mouse.
     * @param {cn_mouse_event} mouse_event
     * @returns  {boolean}
     */
    move(mouse_event) {
        if (this._edit_box.duplication_mode())
            return this._edit_box.move(mouse_event);

        if (super.move(mouse_event)) return true;

        if (!this._opening)
            return false;

        //*** check measures */
        if (this._find_measure(mouse_event)) return true;

        //*** check abiguities */
        this._ambiguous_box_mouseover = this._ambiguous_boxes.findIndex(b => b.contains_point(mouse_event.mouse_screen));
        if (this._ambiguous_box_mouseover >= 0)
            return true;

        //*** check resize handles */
        if (!this._opening.locked) {
            for (var nv = 0; nv < 2; nv++) {
                let x = this._opening.position;
                if (nv) x += this._opening.get_width();
                let p0 = this._opening.wall.compute_side_position_from_abcissa(x, 0);
                let p1 = this._opening.wall.compute_side_position_from_abcissa(x, 1);
                if (mouse_event.camera.on_edge(mouse_event.mouse_world, p0, p1)) {
                    this._mouseover = 20 + nv;
                    return true;
                }
            }
        }

        //*** check opening for drag */
        if (this._opening && this._opening.contains(mouse_event.mouse_world, mouse_event.camera.snap_world_distance)) {
            this._mouseover_opening = true;
            return true;
        }

        //*** check wall lock */
        this._opening.get_candidate_locks().forEach((v, vindex) => {
            if (v && mouse_event.camera.on_vertex(mouse_event.mouse_world, v.position)) {
                this._mouseover = 100 + vindex;
            }
        });
        if (this._mouseover >= 0) return true;

        //*** check position disk */
        if (this._draw_disk) {
            var d = cn_sub(mouse_event.mouse_screen, this._center);
            if (cn_size(d) < this._radius) {
                const x = cn_dot(d, this._dx);
                const y = cn_dot(d, this._dy);
                this._mouseover = 0;
                if (x < 0) this._mouseover = 1;
                if (y < 0) this._mouseover = 3 - this._mouseover;
                return true;
            }
        }

        return false;
    }

    _find_measure(ev) {
        if (!this._draw_measures) return false;
        if (!this._opening || !this._opening.valid)
            return false;
        this._opening_measures.forEach(m => m.selected = false);
        this._opening_dimensions.selected = false;
        this._mouseover_measure = [this._opening_dimensions, ...this._opening_measures].find(m => Math.abs(m.position[0] - ev.mouse_screen[0]) <= 30 && Math.abs(m.position[1] - ev.mouse_screen[1]) <= 10);
        if (this._mouseover_measure) {
            this._mouseover_measure.selected = true;
            return true;
        }
        return false;
    }
}

